import React, { useCallback, useEffect, useState } from "react";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";

import AppToast from "../Toasts/AppToast";
import { useToast } from "../Toasts/useToast";
import { getWhatsAppStatus, listWhatsAppTemplates } from "../../whatsappApi";
import { userCanConfigureWhatsApp } from "./access";
import usePracticeOptions from "./usePracticeOptions";
import WhatsAppConnectCard from "./WhatsAppConnectCard";
import WhatsAppTemplateList from "./WhatsAppTemplateList";
import WhatsAppReminderMapping from "./WhatsAppReminderMapping";

/**
 * WhatsAppSettings: the "Communications > WhatsApp" panel in PracticeSettings.
 *
 * Layout per /CLAUDE.md §1.2: Container fluid + .acc-list, heading panel on the
 * left at xl, content on the right.
 */
export default function WhatsAppSettings() {
  const { options: practices, practiceNumbers, labelFor } = usePracticeOptions();
  const canConfigure = userCanConfigureWhatsApp();
  const { toast, showToast, hideToast } = useToast();

  const [practiceNumber, setPracticeNumber] = useState(practiceNumbers[0] || "");
  const [account, setAccount] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [standardSet, setStandardSet] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!practiceNumber) return;
    setLoading(true);
    try {
      const status = await getWhatsAppStatus(practiceNumber);
      setAccount(status);

      if (status?.connected) {
        const data = await listWhatsAppTemplates(practiceNumber);
        setTemplates(data.templates || []);
        setStandardSet(data.standardSet || null);
      } else {
        setTemplates([]);
        setStandardSet(null);
      }
    } catch (err) {
      showToast(
        "danger",
        err?.response?.data?.error || "Could not load WhatsApp settings.",
      );
    } finally {
      setLoading(false);
    }
  }, [practiceNumber, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <Container fluid className="pt-2 acc-list">
        <Row>
          <Col xs={12} xl={3}>
            <h5 className="text-primary mb-2">WhatsApp</h5>
            <p>
              <small className="text-muted">
                Connect this practice's WhatsApp Business account to send
                appointment reminders and chat with patients from the dashboard.
              </small>
            </p>

            {practices.length > 1 && (
              <Form.Group className="mb-2">
                <Form.Label>Practice</Form.Label>
                <Form.Select
                  value={practiceNumber}
                  onChange={(e) => setPracticeNumber(e.target.value)}
                >
                  {practices.map((p) => (
                    <option key={p.practiceNumber} value={p.practiceNumber}>
                      {p.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
          </Col>

          <Col xs={12} xl={9}>
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2">Loading WhatsApp settings…</p>
              </div>
            ) : (
              <>
                <WhatsAppConnectCard
                  practiceName={labelFor(practiceNumber)}
                  practiceNumber={practiceNumber}
                  account={account}
                  canConfigure={canConfigure}
                  onChanged={load}
                  showToast={showToast}
                />

                {account?.connected && (
                  <>
                    <WhatsAppTemplateList
                      practiceNumber={practiceNumber}
                      templates={templates}
                      standardSet={standardSet}
                      canConfigure={canConfigure}
                      onChanged={load}
                      showToast={showToast}
                    />
                    <WhatsAppReminderMapping
                      practiceNumber={practiceNumber}
                      templates={templates}
                      canConfigure={canConfigure}
                      showToast={showToast}
                    />
                  </>
                )}
              </>
            )}
          </Col>
        </Row>
      </Container>

      <AppToast
        show={toast.show}
        onClose={hideToast}
        variant={toast.variant}
        message={toast.message}
      />
    </>
  );
}
